import React, { PureComponent } from "react";
import {
  type LoadingPresentationBase,
  resolveLoadingPresentation
} from "../../../../../shared/utility/first-load-skeletons";
import { KModal } from "../../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../../ui-kit/k-modal/k-modal-footer";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import { KuikaSkeletonList, KuikaSkeletonRegion } from "../../../../ui-kit/kuika-skeleton/kuika-skeleton";
import "./mcp-chat-history-modal.scss";
import {
  formatDdlOp,
  formatRelativeTime,
  formatSummaryLines,
  type CheckpointDescriptor,
  type RewindSummaryLine
} from "./mcp-chat-rewind";

interface McpChatHistoryModalProps {
  checkpoints: CheckpointDescriptor[];
  /** Currently active tab id — checkpoints on other tabs render with a
   *  distinctive marker so the user knows revert will switch chats. */
  activeTabId: string;
  isLoading: boolean;
  errorText: string | null;
  onSelectCheckpoint: (userMsgId: string, tabId: string) => void;
  onClose: () => void;
}

/** One date bucket of the timeline: its header and the checkpoints under it, newest first. */
interface ICheckpointDayGroup {
  label: string;
  items: CheckpointDescriptor[];
}

interface McpChatHistoryModalState {
  /** userMsgId of the currently-expanded checkpoint; null when collapsed. */
  expandedId: string | null;
  /** Flips to true on first paint — drives the one-shot staggered reveal. */
  mounted: boolean;
}

/**
 * Editorial-style timeline of every revert point in the project. A vertical
 * spine anchors the flow, with each checkpoint rendered as a dated "entry"
 * the user can expand for details and roll back from. Kuika palette only —
 * the composition is hand-rolled so the visual language can diverge from
 * the standard form/modal look without fighting design-system primitives.
 *
 * Final action always routes through the existing revert confirmation dialog.
 * This modal is a browser, not a committer.
 */
export default class McpChatHistoryModal extends PureComponent<McpChatHistoryModalProps, McpChatHistoryModalState> {
  constructor(props: McpChatHistoryModalProps) {
    super(props);
    this.state = { expandedId: null, mounted: false };
  }

  componentDidMount(): void {
    document.addEventListener("keydown", this.handleKeyDown);
    // Kick the reveal animation on next paint so the staggered
    // `animation-delay` values land after the initial frame settles.
    requestAnimationFrame(() => {
      if (!this.isUnmounting) this.setState({ mounted: true });
    });
  }

  componentWillUnmount(): void {
    this.isUnmounting = true;
    document.removeEventListener("keydown", this.handleKeyDown);
  }

  private isUnmounting = false;

  private handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key === "Escape") {
      e.preventDefault();
      this.props.onClose();
    }
  };

  private toggleExpanded = (id: string): void => {
    this.setState((prev) => ({ expandedId: prev.expandedId === id ? null : id }));
  };

  /**
   * Bucket the newest-first checkpoint list into date groups so the timeline
   * can show "Today / Yesterday / Month Day" headers between streaks of
   * entries from the same day.
   */
  private groupByDay = (checkpoints: CheckpointDescriptor[]): ICheckpointDayGroup[] => {
    const groups: ICheckpointDayGroup[] = [];
    const now = new Date();
    const todayKey = this.dateKey(now);
    const yesterdayKey = this.dateKey(new Date(now.getTime() - 86_400_000));

    const labelFor = (ts: number): string => {
      const d = new Date(ts);
      const key = this.dateKey(d);
      if (key === todayKey) return "Today";
      if (key === yesterdayKey) return "Yesterday";
      const sameYear = d.getFullYear() === now.getFullYear();
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: sameYear ? undefined : "numeric"
      });
    };

    for (const cp of checkpoints) {
      const label = labelFor(cp.timestamp);
      const last = groups[groups.length - 1];
      if (last && last.label === label) last.items.push(cp);
      else groups.push({ label, items: [cp] });
    }
    return groups;
  };

  private dateKey = (d: Date): string => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

  /**
   * Pick the dominant change kind for a checkpoint, used to tint the spine
   * node and card accent. Delete wins over revert wins over neutral.
   */
  private dominantKind = (lines: RewindSummaryLine[]): "delete" | "revert" | "conversation" => {
    if (lines.some((l) => l.kind === "delete")) return "delete";
    if (lines.some((l) => l.kind === "revert")) return "revert";
    return "conversation";
  };

  private renderCountPills = (lines: RewindSummaryLine[]): React.ReactNode => {
    const pills = lines
      .filter((l) => l.kind !== "conversation")
      .map((l, i) => {
        const parts = l.headline.split(" ");
        const count = parts[0];
        const label = parts.slice(1).join(" ");
        return (
          <span key={i} className={`mcp-tl__pill mcp-tl__pill--${l.kind}`}>
            <span className="mcp-tl__pill-count">{count}</span>
            <span className="mcp-tl__pill-label">{label}</span>
          </span>
        );
      });
    return pills.length > 0 ? <div className="mcp-tl__pills">{pills}</div> : null;
  };

  private renderEntry = (cp: CheckpointDescriptor, absoluteIndex: number): React.ReactNode => {
    const lines = formatSummaryLines(cp.summary);
    const conversationLine = lines.find((l) => l.kind === "conversation") || null;
    const entityLines = lines.filter((l) => l.kind !== "conversation" && l.names.length > 0);
    const hasDdl = cp.summary.ddlOps.length > 0;
    const kind = this.dominantKind(lines);
    const isOtherTab = cp.tabId !== this.props.activeTabId;
    const isExpanded = this.state.expandedId === cp.userMsgId;
    const absoluteTime = new Date(cp.timestamp).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
    // Cap stagger at ~10 so entries 11+ still appear quickly rather than
    // crawling in forever on long histories.
    const staggerStep = Math.min(absoluteIndex, 10);

    return (
      <article
        key={`${cp.tabId}:${cp.userMsgId}`}
        className={`mcp-tl__entry mcp-tl__entry--${kind}${isExpanded ? " mcp-tl__entry--open" : ""}${
          cp.isArchived ? " mcp-tl__entry--archived" : ""
        }`}
        style={{ ["--stagger" as string]: `${staggerStep}` } as React.CSSProperties}
      >
        <div className={`mcp-tl__node mcp-tl__node--${kind}`} aria-hidden>
          <div className="mcp-tl__node-inner" />
        </div>

        <div className="mcp-tl__card">
          <button
            type="button"
            className="mcp-tl__card-head"
            onClick={() => this.toggleExpanded(cp.userMsgId)}
            aria-expanded={isExpanded}
          >
            <div className="mcp-tl__meta">
              <time
                className="mcp-tl__time"
                dateTime={new Date(cp.timestamp).toISOString()}
                title={new Date(cp.timestamp).toLocaleString("en-US")}
              >
                <span className="mcp-tl__time-rel">{formatRelativeTime(cp.timestamp)}</span>
                <span className="mcp-tl__time-abs">{absoluteTime}</span>
              </time>
              <div className="mcp-tl__tags">
                <span className={`mcp-tl__tab${isOtherTab ? " mcp-tl__tab--other" : ""}`} title={`From ${cp.tabName}`}>
                  <span className="mcp-tl__tab-dot" aria-hidden />
                  {cp.tabName}
                </span>
                {cp.isArchived && <span className="mcp-tl__closed">Closed</span>}
              </div>
            </div>

            <p className="mcp-tl__preview" title={cp.preview}>
              {cp.preview || <em className="mcp-tl__preview-empty">(empty message)</em>}
            </p>

            {this.renderCountPills(lines)}

            <svg className="mcp-tl__chevron" width="16" height="16" viewBox="0 0 24 24" aria-hidden>
              <path
                d="M6 9l6 6 6-6"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <div className="mcp-tl__card-body" aria-hidden={!isExpanded}>
            <div className="mcp-tl__card-body-inner">
              {entityLines.length > 0 && (
                <ul className="mcp-tl__detail-list">
                  {entityLines.map((line, i) => (
                    <li key={i} className={`mcp-tl__detail mcp-tl__detail--${line.kind}`}>
                      <span className="mcp-tl__detail-headline">{line.headline}</span>
                      <ul className="mcp-tl__names">
                        {line.names.map((name, j) => (
                          <li key={j} title={name}>
                            <span className="mcp-tl__name-bullet" aria-hidden>
                              —
                            </span>
                            {name}
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              )}

              {conversationLine && (
                <div className="mcp-tl__conversation-note">
                  <span className="mcp-tl__conversation-icon" aria-hidden>
                    ↩
                  </span>
                  {conversationLine.headline}
                </div>
              )}

              {hasDdl && (
                <div className="mcp-tl__ddl">
                  <div className="mcp-tl__ddl-head">
                    <span className="mcp-tl__ddl-icon" aria-hidden>
                      ⚠
                    </span>
                    Permanent — not reverted
                  </div>
                  <ul className="mcp-tl__ddl-list">
                    {cp.summary.ddlOps.map((op, i) => (
                      <li key={i}>{formatDdlOp(op)}</li>
                    ))}
                  </ul>
                  <p className="mcp-tl__ddl-hint">Schema changes persist after revert. Drop or alter these manually.</p>
                </div>
              )}

              <div className="mcp-tl__actions">
                <KuikaButton
                  type="primary"
                  color="danger"
                  size="md"
                  onClick={() => this.props.onSelectCheckpoint(cp.userMsgId, cp.tabId)}
                  uppercase
                  data-testid={`historyRevertButton-${absoluteIndex}`}
                >
                  REVERT TO HERE
                </KuikaButton>
              </div>
            </div>
          </div>
        </div>
      </article>
    );
  };

  private renderEmpty = (): React.ReactNode => (
    <div className="mcp-tl__empty">
      <div className="mcp-tl__empty-mark">
        <svg width="40" height="40" viewBox="0 0 24 24" aria-hidden>
          <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="2 3" />
          <path d="M12 7v5l3 2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </div>
      <h4 className="mcp-tl__empty-title">No revert points yet</h4>
      <p className="mcp-tl__empty-body">
        Send an AI message and each checkpoint will appear here,
        <br />
        newest on top.
      </p>
    </div>
  );

  /** The timeline in outline while its first load has nothing to show: a node and two lines per entry. */
  private renderLoading = (): React.ReactNode => (
    <KuikaSkeletonRegion label="Loading revert points" className="mcp-tl__loading">
      <KuikaSkeletonList rows={3} density="comfortable" lines={2} />
    </KuikaSkeletonRegion>
  );

  /**
   * Skeleton only while the modal has no checkpoints of its own; a reload with checkpoints on screen
   * keeps them there.
   */
  private renderBody = (groups: ICheckpointDayGroup[]): React.ReactNode => {
    const { checkpoints, isLoading } = this.props;
    const presentation: LoadingPresentationBase = resolveLoadingPresentation(isLoading, checkpoints.length > 0);

    if (presentation === "skeleton") {
      return this.renderLoading();
    }

    if (checkpoints.length === 0) {
      return this.renderEmpty();
    }

    let absoluteIndex: number = 0;

    return (
      <div className="mcp-tl__timeline">
        <div className="mcp-tl__spine" aria-hidden>
          <span className="mcp-tl__spine-now">NOW</span>
        </div>

        <div className="mcp-tl__entries">
          {groups.map((group: ICheckpointDayGroup, gi: number): JSX.Element => (
            <section key={`g-${gi}`} className="mcp-tl__group">
              <div className="mcp-tl__group-header">
                <span className="mcp-tl__group-rule" aria-hidden />
                <span className="mcp-tl__group-label">{group.label}</span>
                <span className="mcp-tl__group-rule" aria-hidden />
              </div>
              {group.items.map((cp: CheckpointDescriptor): React.ReactNode => {
                const node: React.ReactNode = this.renderEntry(cp, absoluteIndex);
                absoluteIndex += 1;
                return node;
              })}
            </section>
          ))}
        </div>
      </div>
    );
  };

  render(): JSX.Element {
    const { checkpoints, errorText, onClose } = this.props;
    const groups = this.groupByDay(checkpoints);

    const footer = (
      <KModalFooter submitLabel="Close" submitTestId="historyCloseButton" onSubmit={onClose}>
        <span className="mcp-tl__footer-caption">
          {checkpoints.length === 0
            ? ""
            : `${checkpoints.length} ${checkpoints.length === 1 ? "checkpoint" : "checkpoints"} available`}
        </span>
      </KModalFooter>
    );

    return (
      <KModal showModal={true} closeModal={onClose} hideHeader footer={footer} width={640} centered>
        <div className={`mcp-tl${this.state.mounted ? " mcp-tl--mounted" : ""}`} data-variant="timeline">
          <button type="button" className="mcp-tl__close" onClick={onClose} aria-label="Close history">
            <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden>
              <path d="M18 6L6 18M6 6l12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>

          <header className="mcp-tl__header">
            <div className="mcp-tl__header-kicker">REVERT</div>
            <h2 className="mcp-tl__header-title">
              History
              <span className="mcp-tl__header-title-mark" aria-hidden>
                <span />
                <span />
                <span />
              </span>
            </h2>
            <p className="mcp-tl__header-sub">Walk back along the timeline and drop in at any earlier moment.</p>
          </header>

          {this.renderBody(groups)}

          {errorText && <div className="mcp-tl__error">{errorText}</div>}
        </div>
      </KModal>
    );
  }
}
